import type { AssistantProjectRow } from '@/components/ProjectAssistant/AssistantInbox';
import type { UpdateCategory } from './projectUpdateCategories';
export type PreviewCategory = 'all' | UpdateCategory;
export type ProjectUpdatePreview = { rows: AssistantProjectRow[]; count: number; complete: boolean; checked: number; total: number; savedAt: number };
const previews = new Map<string, ProjectUpdatePreview>(), listeners = new Set<() => void>();
const ttl = 10 * 60 * 1000, limit = 20;
let activeOwner = '', version = 0;
const key = (owner: string, term: string, category: PreviewCategory) => JSON.stringify([owner, term, category]);
const changed = () => { version++; listeners.forEach(listener => listener()); };
export const subscribeUpdatePreviews = (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; };
export const updatePreviewVersion = () => version;
export function clearUpdatePreviews() { previews.clear(); changed(); }
export function activatePreviewOwner(owner: string) { if (owner !== activeOwner) { activeOwner = owner; clearUpdatePreviews(); } }
/** Memory-only read previews: no mail/file names in persistent browser storage, no consumer checkpoints. */
export function readUpdatePreview(owner: string, term: string, category: PreviewCategory, now = Date.now()) {
  if (!owner || owner !== activeOwner) return undefined;
  const value = previews.get(key(owner, term, category));
  return value && now - value.savedAt < ttl ? value : undefined;
}
export function saveUpdatePreview(owner: string, term: string, category: PreviewCategory, value: Omit<ProjectUpdatePreview, 'savedAt'>) {
  if (!owner || owner !== activeOwner) return;
  const target = key(owner, term, category), previous = readUpdatePreview(owner, term, category);
  // Do not replace a useful previous preview with a smaller unfinished read's zero.
  if (previous && previous.total === value.total && !value.complete && (value.checked < previous.checked || value.count === 0 && previous.count > 0)) return;
  previews.delete(target); previews.set(target, { ...value, rows: value.rows.slice(0, 10), savedAt: Date.now() });
  while (previews.size > limit) previews.delete(previews.keys().next().value!);
  changed();
}
export function previewCount(value: ProjectUpdatePreview | undefined) {
  return !value || !value.complete && value.count === 0 ? '—' : `${value.count}${value.complete ? '' : '+'}`;
}
export function invalidateUpdatePreviews(owner: string) {
  if (owner !== activeOwner) return;
  clearUpdatePreviews();
}

export function saveCategorizedPreviews(owner: string, term: string, records: Array<{ categories: UpdateCategory[]; row: AssistantProjectRow }>, total: number, complete: boolean) {
  for (const category of ['needs_review', 'has_changes', 'needs_recheck', 'no_changes'] as const) {
    const rows = records.filter(record => record.categories.includes(category)).map(record => record.row);
    saveUpdatePreview(owner, term, category, { rows, count: rows.length, checked: records.length, total, complete });
  }
}
